<script lang="ts">
export default {
  name: 'BulkEditDiffModal',
}
</script>
<script setup lang="ts" >
import { useI18n } from 'vue-i18n'
import { computed, ref, watch } from 'vue'
import { KkModal, KkIcon, KkButton, KkLoading } from '../../../components/base'
import { KkTabList } from '../../../components/ui'
import { PaymentMethodPreviewTableRaws } from '../../bulkEdit'
import type { IconName } from 'src/biz_spa/components/base/KkIcon/index.vue'
import type { PreviewPerService } from 'src/biz_spa/domains/bulkEdit/types'

const { t } = useI18n()
interface FoodOption {
  lastOrderTime?: string
  kind?: string
}
interface Period {
  openTime: string
  closeTime: string
  foodOption?: FoodOption
}
interface RegularHour {
  open: boolean
  wday: string
  periods: Period[]
}
interface WdayIndexRegularHour {
  open: boolean
  indexes: number[]
  wdays: string[]
  periods: Period[]
}
interface AdditionalText {
  context: string
}
interface SpecialHour {
  open: boolean
  date: string
  periods: Period[]
}
interface SiteSetting {
  store_gmb_site_setting: {
    web_site_url: string
  }
}
interface GmbLocation {
  address_synced: boolean
  business_hours_synced: boolean
  category_synced: boolean
  description_synced: boolean
  name_synced: boolean
  open_info_synced: boolean
  payment_method_synced: boolean
  phone_synced: boolean
  website_url_synced: boolean
}
interface Query {
  store_regular_hours: RegularHour[]
  store_holiday_regular_hour: RegularHour
  store_day_before_holiday_regular_hour: RegularHour
  store_wday_index_regular_hours: WdayIndexRegularHour[]
  store_regular_hour_additional_description: AdditionalText
  store_regular_holiday_additional_description: AdditionalText
  store_special_hours: SpecialHour[]
  store_site_setting?: SiteSetting
  store_gmb_location: Partial<GmbLocation>
}

interface Props {
  isVisible: boolean
  loading: boolean
  query: Query
  storeCount: number
  connectedServices: ServiceType[]
  preview: PreviewPerService
}
interface InsertTableRow {
  label?: string,
  siteName?: string
  siteDiffCount?: number
  valueDiffCount?: number
  afterValue: string
  icon?: IconName
}
const props = defineProps<Props>()
const emit = defineEmits(['submit', 'close'])
interface Kind { [type: string]: string }
const ServiceTypes = ['google', 'tabelog', 'gnavi', 'hotPepperGourmet', 'retty', 'tripAdvisor', 'yahooPlace'] as const
type ServiceType = typeof ServiceTypes[number]
const kind: Kind = { morning: 'モーニング', lunch: 'ランチ', dinner: 'ディナー' }

const selectableServiceTypes = computed<ServiceType[]>(() => {
  return ServiceTypes.filter(serviceType => props.preview[serviceType])
})

const selectableServiceTypeTabs = computed(() => {
  const result: { [type: string]: string } = {}
  selectableServiceTypes.value.forEach(key => result[key] = t(`labels.sites.${key}`))
  return result
})

const errorServiceTypes = computed<string[]>(() => {
  const result: string[] = []
  Object.entries(props.preview).forEach(([key, value]) => {
    if (value?.errors?.length) result.push(key)
  })
  return result
})

const hasError = computed<boolean>(() => {
  return !!errorServiceTypes.value.length
})

const errors = computed<string[]>(() => {
  if (selectedServiceType.value == null) return []

  return props.preview[selectedServiceType.value]?.errors || []
})

const selectedServiceType = ref<ServiceType>()
/** Watch */
watch(selectableServiceTypes, () => {
  if (!selectableServiceTypes.value.length) return
  selectedServiceType.value = selectableServiceTypes.value[0]
})

const changeSelectedServiceType = (value: ServiceType): void => {
  selectedServiceType.value = value
}

const diffTableRow = computed((): InsertTableRow[] => {
  const site = selectedServiceType.value
  if (site == null) return []

  let returnResult: InsertTableRow[] = []
  const regularHours = props.query.store_regular_hours
  const holidayHour = props.query.store_holiday_regular_hour
  const dayBeforeHolidayHour = props.query.store_day_before_holiday_regular_hour
  const wdayIndexRegularHours = props.query.store_wday_index_regular_hours
  const regularHourAdditionalDescription = props.query.store_regular_hour_additional_description
  const regularHolidayAdditionalDescription = props.query.store_regular_holiday_additional_description
  const specialHours = props.query.store_special_hours
  const siteSetting = props.query.store_site_setting
  const gmbLocation = props.query.store_gmb_location

  let result: InsertTableRow[] = []
  if(Object.keys(regularHours).length){
    const value = regularHours.map((v, i) => {
      const row: InsertTableRow = { afterValue: formatRegularHourText(v, site) }
      if(i === 0) {
        row.label = t('models.storeRegularHour.modelName')
        row.valueDiffCount = regularHours.length
      }
      return row
    })
    result = result.concat(value)
  }
  let holidayHours = []
  if (holidayHour) holidayHours.push({hour: holidayHour, type: 'holiday'})
  if (dayBeforeHolidayHour) holidayHours.push({hour: dayBeforeHolidayHour, type: 'dayBeforeHoliday'})
  if (Object.keys(holidayHours).length) {
    const value = holidayHours.map((v, i) => {
      const row: InsertTableRow = { afterValue: formatHolidayRegularHourText(v.hour, v.type, site) }
      if(i === 0) {
        row.label = t('models.storeHolidayRegularHour.modelName')
        row.valueDiffCount = holidayHours.length
      }
      return row
    })
    result = result.concat(value)
  }
  if (Object.keys(wdayIndexRegularHours).length && site !== 'yahooPlace') {
    const value = wdayIndexRegularHours.map((v, i) => {
      const row: InsertTableRow = { afterValue: formatWdayIndexRegularHourText(v, site) }
      if(i === 0) {
        row.label = t('models.storeWdayIndexRegularHour.modelName')
        row.valueDiffCount = wdayIndexRegularHours.length
      }
      return row
    })
    result = result.concat(value)
  }
  if (regularHourAdditionalDescription) {
    const row: InsertTableRow = { afterValue: regularHourAdditionalDescription.context }
    row.label = t('models.storeRegularHourAdditionalDescription.modelName')
    row.valueDiffCount = 1
    result = result.concat([row])
  }
  if (regularHolidayAdditionalDescription && site !== 'yahooPlace') {
    const row: InsertTableRow = { afterValue: regularHolidayAdditionalDescription.context }
    row.label = t('models.storeRegularHolidayAdditionalDescription.modelName')
    row.valueDiffCount = 1
    result = result.concat([row])
  }
  if(Object.keys(specialHours).length){
    const value = specialHours.map((v, i) => {
      const row: InsertTableRow = { afterValue: formatSpecialHourText(v) }
      if(i === 0) {
        row.label = t('models.storeSpecialHour.modelName')
        row.valueDiffCount = specialHours.length
      }
      return row
    })
    result = result.concat(value)
  }
  if(siteSetting?.store_gmb_site_setting && site === 'google') {
    result = result.concat({
      label: t('pages.CompaniesLocationStore.siteSetting.form.websiteUrl'),
      valueDiffCount: 1,
      afterValue: siteSetting.store_gmb_site_setting.web_site_url
    })
  }
  if(gmbLocation && site === 'google') {
    const value = Object.keys(gmbLocation).map((v, i) => {
      type Key = keyof GmbLocation
      const key: Key = v as Key
      const row: InsertTableRow = { afterValue: t(`pages.CompaniesLocationStoresBulkEdit.gmbLocation.kindLabels.${key}`) }
      if(i === 0) {
        row.label = t('pages.CompaniesLocationStoresBulkEdit.gmbLocation.kindLabels.title')
        row.valueDiffCount = Object.keys(gmbLocation).length
      }
      if(gmbLocation[key] !== null) row.icon = gmbLocation[key] ? 'iconOk' : 'iconNg'
      return row
    })
    result = result.concat(value.filter((v): v is InsertTableRow  => v !== undefined))
  }
  if(result.length > 0) {
    result[0].siteName = t(`labels.sites.${site}`)
    result[0].siteDiffCount = result.length
    returnResult = returnResult.concat(result)
  }

  return returnResult
})

const formatRegularHourText = (obj: RegularHour, site: ServiceType): string => {
  const wday = obj.wday
  if (!obj.periods) return t('labels.shortWeekday.' + wday) + ' ' + t('pages.CompaniesLocationStoresBulkEdit.diffModal.changeAfter')
  if (!obj.open) return t('labels.shortWeekday.' + wday) + ' ' + t('models.storeRegularHour.regularHoliday')
  const texts = obj.periods.map((period, index) => {
    if (site === 'yahooPlace' && index > 2) return null
    if (period.openTime === '00:00' && period.closeTime === '24:00') {
      return t('labels.shortWeekday.' + wday) + ' ' + t('models.storeRegularHour.open24hours')
    }
    let periodText = t('labels.shortWeekday.' + wday) + ' ' + period.openTime + '〜' + period.closeTime
    if (site !== 'google') periodText += ` ${period.foodOption?.lastOrderTime ? `(LO ${period.foodOption.lastOrderTime})` : ''} ${period.foodOption?.kind ? kind[period.foodOption.kind] : ''} `
    return periodText
  }).filter(v=>v)
  return texts.join('\n')
}
const formatHolidayRegularHourText = (obj: RegularHour, type: string, site: ServiceType): string => {
  if (!obj.periods) return t('labels.' + type) + ' ' + t('pages.CompaniesLocationStoresBulkEdit.diffModal.changeAfter')
  if (!obj.open) return t('labels.' + type) + ' ' + t('models.storeHolidayRegularHour.regularHoliday')
  const texts = obj.periods.map(period => {
    if (period.openTime === '00:00' && period.closeTime === '24:00') {
      return t('labels.' + type) + ' ' + t('models.storeHolidayRegularHour.open24hours')
    }
    let periodText = t('labels.' + type) + ' ' + period.openTime + '〜' + period.closeTime
    if (!['google', 'yahooPlace'].includes(site)) periodText += ` ${period.foodOption?.lastOrderTime ? `(LO ${period.foodOption.lastOrderTime})` : ''} ${period.foodOption?.kind ? kind[period.foodOption.kind] : ''} `
    return periodText
  })
  return texts.join('\n')
}
interface Week { [type: string]: string }
const week: Week = { sunday: '日曜日', monday: '月曜日', tuesday: '火曜日', wednesday: '水曜日', thursday: '木曜日', friday: '金曜日', saturday: '土曜日' }
const daysOfWeek: string[] = [ 'sunday','monday','tuesday','wednesday','thursday','friday','saturday']
const formatWdayIndexRegularHourText = (obj: WdayIndexRegularHour, site: ServiceType): string => {
  const indexes = obj.indexes
  const wdays = obj.wdays
  indexes.sort((a: number, b: number): number => a - b)
  wdays.sort((a: string, b: string): number => daysOfWeek.indexOf(a) - daysOfWeek.indexOf(b))

  if (!obj.periods) {
    let notSetText = ''
    for (let index of indexes) for (let wday of wdays) {
      notSetText += `第${index}${week[wday]}` + ' ' + t('pages.CompaniesLocationStoresBulkEdit.diffModal.noSet') + '\n'
    }
    return notSetText
  }
  if (!obj.open) {
    let notOpenText = ''
    for (let index of indexes) for (let wday of wdays) {
      notOpenText += `第${index}${week[wday]}` + ' ' + t('models.storeWdayIndexRegularHour.regularHoliday') + '\n'
    }
    return notOpenText
  }

  let periodText = ''
  for (let index of indexes) for (let wday of wdays) {
    periodText += obj.periods.map(period => {
      if (period.openTime === '00:00' && period.closeTime === '00:00') {
        return `第${index}${week[wday]}` + ' ' + t('models.storeWdayIndexRegularHour.open24hours') + ' '
      }
      let periodText = `第${index}${week[wday]}` + ' ' + period.openTime + '〜' + period.closeTime
      if (site !== 'google') periodText += ` ${period.foodOption?.lastOrderTime ? `(LO ${period.foodOption.lastOrderTime})` : ''} ${period.foodOption?.kind ? kind[period.foodOption.kind] : ''} `
      return periodText
    }).join('\n')
  }
  return periodText
}
const formatSpecialHourText = (obj: SpecialHour): string => {
  const date = obj.date
  if (!obj.periods) return date + ' ' + t('pages.CompaniesLocationStoresBulkEdit.diffModal.noSet')
  if (!obj.open) return date + ' ' + t('models.storeSpecialHour.regularHoliday')
  return obj.periods.map(period => {
    if (period.openTime === '00:00' && period.closeTime === '00:00') {
      return date + ' ' + t('models.storeRegularHour.open24hours')
    }
    return date + ' ' + period.openTime + '〜' + period.closeTime
  }).join('\n')
}
</script>

<template>
  <KkModal :is-visible="props.isVisible" width="1076px" @change="emit('close')">
    <div class="p-modal__content__header">
      <p>
        {{ $t('pages.CompaniesLocationStoresBulkEdit.diffModal.header2') }}
        <span class="store-amounts">{{ props.storeCount + $t('models.store.modelName') }}</span>）
        <span v-if="hasError" class="diffModal__header--error">
          {{ t('pages.CompaniesLocationStoresBulkEdit.diffModal.hasError') }}
        </span>
      </p>
    </div>
    <KkTabList
      class="diffModal__tabs"
      :menu-list="selectableServiceTypeTabs"
      :selected-tab-key="selectedServiceType || ''"
      :icon-keys="errorServiceTypes"
      @click-menu="changeSelectedServiceType($event as ServiceType)"
    >
      <template #icon>
        <KkIcon name="exclamation" color="#E9446A" class="diffModal__tabs__icon" />
      </template>
    </KkTabList>
    <div class="p-modal__content__body">
      <KkLoading v-if="loading" />
      <div v-else class="antiMargin">
        <div class="tableWrapper">
          <div class="p-table">
            <div class="p-table__content">
              <table>
                <tbody class="diffModal">
                  <!-- 決済手段以外 -->
                  <template
                    v-for="(row, i) in diffTableRow"
                    :key="i"
                  >
                    <tr>
                      <td v-if="row.label" :rowSpan="row.valueDiffCount" class="diffModal__label">
                        {{ row.label }}
                      </td>
                      <td>
                        {{ row.afterValue }}
                        <KkIcon v-if="row.icon" class="diffModal__icon" :name="row.icon" />
                      </td>
                    </tr>
                  </template>
                  <!-- 決済手段 -->
                  <template v-if="selectedServiceType">
                    <PaymentMethodPreviewTableRaws
                      :service-type="selectedServiceType"
                      :payment-method-preview="props.preview"
                    />
                  </template>
                </tbody>
              </table>
            </div>
          </div>
          <div class="error">
            <p v-for="(error, index) in errors" :key="index">{{ error }}</p>
          </div>
        </div>
      </div>
    </div>
    <div class="p-modal__content__footer">
      <div class="c-button c-button--right">
        <KkButton
          class="footerCancelBtn"
          color="secondary"
          outlined
          :text="t('labels.close')"
          @click="emit('close')"
        />
        <KkButton
          color="primary"
          outlined
          :disabled="hasError || loading"
          :text="t('labels.confirm')"
          @click="emit('submit')"
        />
      </div>
    </div>
  </KkModal>
</template>

<style lang="scss" scoped>
@import "../../../../../stylesheets/biz_spa/foundation/_var.scss";
.diffModal {
  &__header {
    &--error {
      margin-left: 24px;
      color: $color-accent-primary;
      font-weight: normal;
      font-size: 14px;
    }
  }
  &__tabs {
    padding: 16px 32px;
    &__icon {
      margin-left: 4px;
    }
  }
  &__label {
    background-color: #F8F9FA;
  }
  &__icon {
    width: 15px;
    margin-left: 5px;
  }
  td:empty {
    display: table-cell;
  }
}
.antiMargin {
  margin: -24px -32px 0;
}
.tableWrapper {
  margin: 0 32px;
}
.footerCancelBtn {
  margin-right: 8px;
}
.error {
  margin-top: 16px;
  color: $color-accent-primary;
}
</style>
